/**
 * Validation component and utilities for handling form validation errors.
 */

import type { TemplateOnlyComponent } from '@ember/component/template-only';
import { on } from '@ember/modifier';
import { tracked } from '@glimmer/tracking';

import { DeepSaveError } from '../data/deep-save.ts';

/**
 * Represents a single validation failure with an optional attribute name.
 */
export type ValidationFailure = {
  /** The error message to display to the user */
  message: string;
  /** Optional attribute/field name that this validation failure is associated with */
  attribute?: string;
  /** Optional form label used to locate the field in the DOM */
  fieldLabel?: string;
  /** Optional coach profile section id for nested fields */
  sectionId?: string;
  /** Optional zero-based row index inside repeatable sections */
  rowIndex?: number;
};

/**
 * A reactive validation state container that tracks validation failures.
 *
 * Provides methods to check validation status, find failures by attribute,
 * and dismiss validation errors.
 */
export class Validation {
  /** Array of validation failures, tracked for reactivity */
  @tracked failures: ValidationFailure[] = [];

  /**
   * Private constructor - use static factory methods instead.
   * @param failures - Array of validation failures to initialize with
   */
  private constructor(failures: ValidationFailure[]) {
    this.failures = failures;
  }

  /**
   * Returns true if there are no validation failures (validation passed).
   */
  get isSuccess() {
    return this.failures.length === 0;
  }

  /**
   * Returns true if there are any validation failures (validation failed).
   */
  get isFailure() {
    return !this.isSuccess;
  }

  /**
   * Finds the first validation failure associated with a specific attribute.
   * @param attribute - Attribute name to search for
   * @returns The matching ValidationFailure, or undefined if not found
   */
  attribute = (attribute: string) => {
    return this.failures.find((failure) => failure.attribute === attribute);
  };

  /**
   * Creates a Validation instance representing successful validation (no failures).
   * @returns A Validation instance with no failures
   */
  static success(): Validation {
    return new Validation([]);
  }

  /**
   * Creates a Validation instance from an array of validation failures.
   * @param failures - Array of validation failures
   * @returns A Validation instance containing the provided failures
   */
  static fromFailures(failures: ValidationFailure[]): Validation {
    return new Validation(failures);
  }

  /**
   * Creates a Validation instance from an Error object.
   * If the error is already a Validation instance, extracts its failures.
   * Otherwise, creates a single failure from the error message.
   * @param error - The error to convert to a Validation
   * @returns A Validation instance representing the error
   */
  static fromError(error: Error): Validation {
    if (error instanceof DeepSaveError) {
      const failures = error.errors.flatMap((saveError) => {
        if (
          saveError.error &&
          typeof saveError.error === 'object' &&
          'content' in saveError.error
        ) {
          const content = saveError.error.content as {
            errors: {
              detail:
                | {
                    message: string;
                  }[]
                | {
                    message: string;
                  };
            }[];
          };
          if (Array.isArray(content.errors)) {
            const parsed = content.errors.flatMap((entry) => {
              const details = Array.isArray(entry.detail)
                ? entry.detail
                : entry.detail
                  ? [entry.detail]
                  : [];

              return details
                .map((detail) => detail.message?.trim())
                .filter((message): message is string => Boolean(message))
                .map((message) => {
                  const fieldLabel =
                    message.match(
                      /^"([^"]+)"\s+is not allowed to be empty/i
                    )?.[1] ?? message.match(/^"([^"]+)"\s+is required/i)?.[1];

                  return {
                    message,
                    fieldLabel,
                  };
                });
            });

            // Only use the parsed detail messages if we actually extracted
            // any — otherwise fall through to the generic fallback below so a
            // save failure is never reported as an empty (success) validation.
            if (parsed.length > 0) {
              return parsed;
            }
          }
        }
        if (saveError.error instanceof Error && saveError.error.message) {
          return [
            {
              message: `Failed to save ${saveError.recordType}: ${saveError.error.message}`,
            },
          ];
        }

        return [{ message: `Failed to save ${saveError.recordType}` }];
      });

      // Never return an empty failure set for a save error — that would read
      // as a successful validation.
      if (failures.length === 0) {
        return new Validation([
          { message: 'Failed to save. Please try again.' },
        ]);
      }

      return new Validation(failures);
    }
    return new Validation([{ message: error.message }]);
  }

  /**
   * Dismisses all validation failures by clearing the failures array.
   * This is tracked, so the UI will automatically update.
   */
  dismiss = () => {
    this.failures = [];
  };
}

/**
 * Template-only component that displays validation error messages.
 *
 * Shows a danger alert with all validation failures when validation has failed.
 * Includes a dismiss button to clear the validation errors.
 * Only renders when @validation.isFailure is true.
 */
export const ValidationMessage: TemplateOnlyComponent<{
  Args: {
    /** The Validation instance to display errors from */
    validation: Validation;
  };
  Element: HTMLDivElement;
}> = <template>
  {{#if @validation.isFailure}}
    <div class="alert alert-danger" ...attributes>
      {{#each @validation.failures as |failure|}}
        <p>
          <iconify-icon inline icon="ph:warning-bold" />
          {{failure.message}}
        </p>
      {{/each}}
      <button
        type="button"
        class="btn btn-sm btn-white"
        {{on "click" @validation.dismiss}}
      >
        <iconify-icon inline icon="ph:x-bold" />
        Dismiss
      </button>
    </div>
  {{/if}}
</template>;
